// Auto-scroll hook for chat messages
//
// The rule: follow the conversation down, but never past the start of the
// agent's turn. A message the user sends scrolls to the bottom; a reply scrolls
// up into view until its first line reaches the top of the list, then stays
// there, and the rest is the reader's to scroll to. Nobody reads a reply from
// its last line up.
//
// A turn starts at the last message the user sent themselves (`data-turn-start`,
// also on a session break). What the agent says after it, however many messages
// that is, is one turn: the opening of Chat with Settings and the reply to the
// wizard's screenshot that streams in under it are read from the opening's
// first line, because the screenshot was sent for the user, not by them. With
// no such message yet the turn starts at the top of the list.
//
// Three separate questions, kept separate on purpose:
//
//  1. Is the reader FOLLOWING? — `userFollowing`. Their intent, and only they
//     can change it, by scrolling. Tracked from a real `scroll` listener,
//     because that is the only event a person's decision to leave the bottom
//     actually produces. Sending a message starts following again.
//  2. Where to? — `followTarget()`: the bottom, stopped at the start of the
//     turn, unless the reader scrolled to the tail of the reply THEMSELVES, which
//     is asking to follow it (`followingTail`). Only their own wheel, touch, key
//     or scrollbar drag counts. We only ever scroll down: a reader further down
//     than the turn's start got there on purpose.
//  3. Did the page grow AFTER we decided? — images. An <img> is inserted with no
//     intrinsic height and grows the scroller when it decodes, which is neither
//     a DOM mutation nor a scroll: nothing fires, and the reader has been pushed
//     up the page without touching anything. So we re-scroll when it settles,
//     asking (1) at load time rather than freezing its answer at insert time.
//
// Scrolls are instant rather than smooth while a generation is live: repeated
// *smooth* scrolls retargeting each other on every token read as flashing.

// How close to the bottom counts as "the user is following along".
const NEAR_BOTTOM_THRESHOLD_PX = 120

// How far above the end of the user's message the turn's start sits. The
// toolbar covers the top 80px of the list (its `pt-20`), so this leaves the
// tail of their message showing above the first line of the reply.
const TURN_LEAD_PX = 100

// A message the user sent themselves, or a session break (`MessageItem`).
const TURN_START_SELECTOR = '[data-turn-start]'

// How long a scroll WE started may still be in flight. A smooth scroll fires
// `scroll` repeatedly on its way down, from positions that are nowhere near the
// bottom; read naively, our own scrolling would be mistaken for the user
// scrolling away and would switch auto-scroll off. Within this window a scroll
// event can only confirm following, never revoke it. Comfortably longer than a
// smooth scroll takes, and irrelevant while streaming, where we scroll
// instantly and the very next event lands at the bottom and closes it.
const AUTO_SCROLL_SETTLE_MS = 600

// How long after the reader's last wheel, touch or scroll key a `scroll` event
// still counts as theirs. Wheel and key scrolls are smoothed by the browser and
// keep firing for a while after the input that started them.
const READER_SCROLL_WINDOW_MS = 800

// Keys that scroll the list when it, or nothing editable, has focus.
const SCROLL_KEYS = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'End', 'Home', ' '])

// The element AutoScroll measures to size the message list's bottom padding, and
// the property it publishes the measurement as. Both halves of a contract with
// the composer (see `InputArea`) and the scroller's own `padding-bottom`, so
// they are named here rather than spelled inline: a rename that misses one end
// leaves the composer permanently covering the last message.
const INPUT_AREA_SELECTOR = '[data-input-area]'
const INPUT_HEIGHT_PROPERTY = '--wb-input-height'

const AutoScroll = {
  mounted () {
    // Flag to track if this is the first scroll (on mount)
    this.isFirstScroll = true

    // We open where we would have followed to, so the reader starts out following.
    this.userFollowing = true
    this.pendingAutoScroll = false
    this.boundImages = new WeakSet()
    // Every image listener we add hangs off this, so teardown removes the lot in
    // one call — the alternative is keeping a strong reference to every <img>
    // this scroller has ever seen, which is a leak in a long conversation.
    this.imageListeners = new AbortController()

    this.followingTail = false
    this.chatId = this.el.dataset.chatId
    this.listenForReaderInput()

    // Open at the start of the last turn (the bottom when it fits), without
    // animation.
    this.scrollToFollow(false)

    this.onScroll = () => this.recordScrollIntent()
    this.el.addEventListener('scroll', this.onScroll, { passive: true })

    // Images already on screen at mount (a reloaded conversation) still have to
    // be bound: the observer below only ever sees what arrives after it.
    for (const img of this.el.querySelectorAll('img')) { this.bindImage(img) }

    // Auto-scroll on content changes
    this.observer = new MutationObserver((mutations) => {
      if (this.userFollowing) {
        this.scrollToFollow(true)
      }

      this.bindAddedImages(mutations)
    })

    this.observer.observe(this.el, {
      childList: true,
      subtree: true,
      characterData: true
    })

    // Watch for changes in input area (e.g., attachment previews, textarea expansion)
    const inputArea = document.querySelector(INPUT_AREA_SELECTOR)
    if (inputArea) {
      // Update CSS variable with input area height
      //
      // The following test MUST be taken before the variable is written. The
      // scroller pads its own bottom by the published height, so growing the
      // composer — attaching a file is the big one, the preview is ~200px —
      // grows `scrollHeight` by the same amount. Measured after the write, this
      // asked "are they following along?" about a layout its own line had just
      // changed and decided no. (Reading `userFollowing` rather than
      // re-measuring makes that ordering less critical than it was, but the
      // scroll it triggers still wants the new padding in place first.)
      const updateInputHeight = () => {
        const height = inputArea.offsetHeight
        const following = this.userFollowing

        document.documentElement.style.setProperty(
          INPUT_HEIGHT_PROPERTY,
          `${height}px`
        )

        if (following) {
          this.scrollToFollow(true)
        }
      }

      // Initial height set
      updateInputHeight()

      // Attributes on the element ITSELF, and nothing else. The composer hides
      // itself with a class while a scripted onboarding step owns the turn, and
      // that is a change to the watched element, invisible to a childList
      // observer — without it the message list keeps padding the bottom by the
      // height of a composer that is no longer on screen. Everything else that
      // used to be watched here (children arriving, the box growing as it is
      // typed into) changes the element's SIZE, which the ResizeObserver below
      // already reports; subtree+attributes meant every keystroke's class change
      // on any descendant re-measured the composer and forced a reflow.
      this.inputObserver = new MutationObserver(() => {
        updateInputHeight()
      })

      this.inputObserver.observe(inputArea, {
        attributes: true,
        attributeFilter: ['class']
      })

      // Watch for size changes (textarea expansion, attachments arriving)
      this.resizeObserver = new ResizeObserver(() => {
        updateInputHeight()
      })

      this.resizeObserver.observe(inputArea)
    } else {
      // Loud, because the failure is silent otherwise: the padding falls back to
      // a guess forever and the composer sits on top of the last message.
      console.warn(
        `[AutoScroll] no ${INPUT_AREA_SELECTOR} element — the message list will ` +
        'fall back to its default bottom padding and the composer may cover the ' +
        'last message'
      )
    }

    // Messages broadcast from another panel (`scroll_to_bottom`, named for
    // what it used to do). Not gated on following: the server asked.
    this.handleEvent('scroll_to_bottom', () => {
      // Use requestAnimationFrame to ensure DOM has updated before scrolling
      requestAnimationFrame(() => {
        this.scrollToFollow(true)
      })
    })

    // The user sent a message (`scroll_to_message`, sent with the reply it
    // started): a new turn, which follows whatever they had scrolled to.
    this.handleEvent('scroll_to_message', () => {
      requestAnimationFrame(() => {
        this.startTurn()
      })
    })

    // The reply ended: whatever arrives after it (choices, a broadcast, a
    // regenerated reply that sends no `scroll_to_message`) stops at its turn's
    // start again, even for a reader who followed this one to its tail.
    this.handleEvent('streaming_complete', () => {
      this.followingTail = false
    })

    // Finish the initial-scroll period after the pending layout settles.
    setTimeout(() => {
      this.isFirstScroll = false
    }, 100)
  },

  // Another conversation in the same panel: the stream is reset under this
  // element rather than remounted, so nothing else would notice.
  updated () {
    if (this.el.dataset.chatId !== this.chatId) {
      this.chatId = this.el.dataset.chatId
      this.openConversation()
    }
  },

  destroyed () {
    this.readerInputListeners?.abort()
    this.el.removeEventListener('scroll', this.onScroll)
    this.observer?.disconnect()
    this.inputObserver?.disconnect()
    this.resizeObserver?.disconnect()
    this.imageListeners?.abort()
    clearTimeout(this.autoScrollSettleTimer)
  },

  // The reader scrolled. Besides their sending a message (`startTurn`), this is
  // the ONLY thing that changes `userFollowing`: content growing below them does
  // not move `scrollTop`, so it cannot reach here and cannot be mistaken for
  // them choosing to leave the bottom.
  recordScrollIntent () {
    const nearBottom = this.isNearBottom()

    if (nearBottom) {
      // Either they came back, or one of our own scrolls arrived. Both end the
      // in-flight window.
      this.pendingAutoScroll = false
      this.userFollowing = true
      // They brought themselves to the tail of the reply: follow it.
      if (this.isReaderScrolling()) { this.followingTail = true }
      return
    }

    // Mid-flight of a scroll we started: not the reader's doing.
    if (this.pendingAutoScroll) { return }

    this.userFollowing = false
    this.followingTail = false
  },

  // Note when the reader is driving the list, so a scroll that reaches the
  // bottom can be told apart from one we started or one a short reply ended at.
  listenForReaderInput () {
    this.readerInputListeners = new AbortController()
    const opts = { capture: true, passive: true, signal: this.readerInputListeners.signal }
    const touched = () => { this.readerScrolledAt = Date.now() }
    const released = () => { this.readerDragging = false }
    this.el.addEventListener('wheel', touched, opts)
    this.el.addEventListener('touchmove', touched, opts)
    // The scrollbar: scroll events keep coming for as long as it is dragged.
    // A press on the list itself rather than on anything in it, which is
    // where an overlay scrollbar's press lands too; one on a message is a
    // click or a text selection. A release outside the window never arrives,
    // so losing focus counts as one.
    this.el.addEventListener('pointerdown', (event) => this.noteScrollbarPress(event), opts)
    document.addEventListener('pointerup', released, opts)
    document.addEventListener('pointercancel', released, opts)
    globalThis.window?.addEventListener('blur', released, opts)
    document.addEventListener('keydown', (event) => this.noteScrollKey(event), opts)
  },

  noteScrollbarPress (event) {
    if (event.target === this.el) { this.readerDragging = true }
  },

  // A key that scrolls the list, pressed where it would: not into the
  // composer, and not on a button, where Space and Enter activate it.
  noteScrollKey (event) {
    if (!SCROLL_KEYS.has(event.key) || isInteractive(event.target)) { return }
    this.readerScrolledAt = Date.now()
  },

  // The user sent a message: follow again, from wherever they were. The scroll
  // events this produces are ours, so the click or key that sent the message
  // must not count as the reader bringing the reply to its tail.
  startTurn () {
    this.userFollowing = true
    this.followingTail = false
    this.readerScrolledAt = undefined
    this.readerDragging = false
    this.scrollToFollow(true)
  },

  // Open the conversation now in the list at the start of its last turn, from
  // wherever the previous one was left: the one scroll allowed to go up.
  openConversation () {
    this.userFollowing = true
    this.followingTail = false
    this.readerScrolledAt = undefined
    this.readerDragging = false
    this.markOwnScroll(true)
    this.el.scrollTo({ top: this.followTarget(), behavior: 'instant' })
  },

  isReaderScrolling () {
    return this.readerDragging === true ||
      Date.now() - (this.readerScrolledAt ?? -Infinity) < READER_SCROLL_WINDOW_MS
  },

  // Bind every <img> that arrived in this batch of mutations.
  //
  // Driven off `addedNodes` rather than re-querying the whole list: the markdown
  // renderer rewrites a streaming reply's innerHTML on a 50ms debounce, so a
  // full rescan was walking every image in the conversation ~20 times a second.
  // For the same reason there is no "already bound" MARKER in the DOM — the
  // elements are thrown away and rebuilt wholesale, so a marker survives nothing
  // and buys nothing. The WeakSet is belt and braces for the case where the same
  // element is re-added.
  bindAddedImages (mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) { continue }

        if (node.tagName === 'IMG') {
          this.bindImage(node)
        } else {
          for (const img of node.querySelectorAll('img')) { this.bindImage(img) }
        }
      }
    }
  },

  // Re-scroll once a newly-inserted image finishes decoding, if the reader is
  // still following at that point.
  //
  // Asked at LOAD time, not at insert time. `userFollowing` is not affected by
  // the image growing the scroller, so the answer is as true then as now — and
  // if they scrolled away in the meantime, it correctly says so.
  bindImage (img) {
    // Already decoded (cached): it contributed its height before we measured, so
    // whatever scrolling was due has already accounted for it.
    if (img.complete) { return }
    if (this.boundImages.has(img)) { return }
    this.boundImages.add(img)

    // `error` as well as `load`: a broken image still resolves to its alt box
    // and still changes the height.
    const settle = () => {
      if (this.userFollowing) { this.scrollToFollow(true) }
    }
    const opts = { once: true, signal: this.imageListeners.signal }
    img.addEventListener('load', settle, opts)
    img.addEventListener('error', settle, opts)
  },

  isStreaming () {
    return this.el.dataset.streaming === 'true'
  },

  isNearBottom () {
    return (
      this.el.scrollHeight - this.el.scrollTop - this.el.clientHeight <
      NEAR_BOTTOM_THRESHOLD_PX
    )
  },

  // Where the current turn starts, in the list's scroll coordinates: just above
  // the end of the last message the user sent, or the top of the list when they
  // have not sent one yet.
  turnStartTop () {
    const starts = this.el.querySelectorAll(TURN_START_SELECTOR)
    const last = starts[starts.length - 1]
    if (!last) { return 0 }

    // Measured against the scroller itself rather than offsetTop, which is
    // relative to the nearest positioned ancestor.
    const end = this.el.scrollTop +
      (last.getBoundingClientRect().bottom - this.el.getBoundingClientRect().top)
    return Math.max(0, end - TURN_LEAD_PX)
  },

  // The bottom, but no further than the start of the turn unless the reader
  // took themselves to its tail.
  followTarget () {
    const bottom = this.el.scrollHeight - this.el.clientHeight
    if (this.followingTail) { return bottom }
    return Math.min(bottom, this.turnStartTop())
  },

  // Open the window in which `scroll` events are ours rather than the reader's.
  //
  // Only worth opening for a long trip down: that is the one that fires events
  // from far-from-bottom positions on its way. Starting from the bottom there is
  // nothing to misread, and arming anyway would swallow a genuine scroll away
  // for the length of the window — the reader would be dragged back by the next
  // mutation. Any event that arrives near the bottom closes it early. Opening a
  // conversation always opens it (`force`): that trip can go up as well as
  // down, and ends wherever its turn starts, bottom or not.
  markOwnScroll (force = false) {
    if (!force && this.isNearBottom()) { return }

    this.pendingAutoScroll = true
    clearTimeout(this.autoScrollSettleTimer)
    this.autoScrollSettleTimer = setTimeout(() => {
      this.pendingAutoScroll = false
    }, AUTO_SCROLL_SETTLE_MS)
  },

  scrollToFollow (animate = true) {
    const top = this.followTarget()
    // Only ever down: a reader past the start of the turn went there themselves.
    if (top <= this.el.scrollTop) { return }

    // Never animate mid-generation: the content below changes on every token
    // and overlapping smooth scrolls are what read as flashing/jitter.
    const smooth = animate && !this.isFirstScroll && !this.isStreaming()
    this.markOwnScroll()
    this.el.scrollTo({ top, behavior: smooth ? 'smooth' : 'instant' })
  }
}

function isInteractive (target) {
  return target?.isContentEditable === true ||
    ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A', 'SUMMARY'].includes(target?.tagName) ||
    target?.getAttribute?.('role') === 'button'
}

export default AutoScroll
